import { getDatabase, ref, get, set, update, remove, push } from 'firebase/database';
import { BlueprintLocation, PhotoModel } from '../types/protocols';
import { StorageUnit, StorageUnitInput } from '../types/storageUnits';
import { StorageUnitType } from '../types/Enums';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { softDeleteFile } from './fileDeletionApi';
import { requireAdmin } from './adminGuard';
import { recordActivity } from './activityApi';

/** Tombstone entity type for unit photos. Shared with iOS FileDeletionHelper. */
export const STORAGE_UNIT_PHOTO_ENTITY_TYPE = 'storageUnitPhoto';

const UNIT_TYPES = Object.values(StorageUnitType) as string[];

/** RTDB rejects `undefined`; drop those keys before writing. */
const stripUndefined = <T extends Record<string, unknown>>(value: T): T => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== undefined) {
            result[key] = entry;
        }
    }
    return result as T;
};

/** For `update()`: `undefined` becomes `null` so cleared fields are removed. */
const nullForUndefined = (value: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        result[key] = entry === undefined ? null : entry;
    }
    return result;
};

const optionalString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length > 0 ? value : undefined;

const optionalNumber = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * Storage units (förråd) at `buildings/{buildingKey}/storageUnits/{unitKey}`.
 * Reads need a logged-in user; every mutation needs an admin (plan D11, like
 * parkingApi). Units are updated with `update()` and explicit nulls so photos
 * and blueprintLocations written by iOS are never clobbered (D3). The drawing
 * is the level's, so there is no blueprint handling here (D4).
 */
export class StorageUnitsApi {
    private db = getDatabase();

    getUnitsPath(buildingKey: string): string {
        return `buildings/${buildingKey}/storageUnits`;
    }

    getUnitPath(buildingKey: string, unitKey: string): string {
        return `${this.getUnitsPath(buildingKey)}/${unitKey}`;
    }

    /** DB path == Storage path for photos, like every other photo in the app. */
    getUnitPhotoPath(buildingKey: string, unitKey: string, photoKey: string): string {
        return `${this.getUnitPath(buildingKey, unitKey)}/photos/${photoKey}`;
    }

    // MARK: - Auth

    private async verifyAdmin(action: string): Promise<string> {
        return requireAdmin(this.db, action);
    }

    private stamp(uid: string): { updatedBy: string; updatedWhen: string } {
        return { updatedBy: uid, updatedWhen: formatDateForStorage(new Date()) };
    }

    // MARK: - Parsing

    parseStorageUnit(data: unknown, key: string): StorageUnit | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;
        // A unit without a level cannot be shown anywhere; skip it rather than guess.
        const levelKey = optionalString(d.levelKey);
        if (!levelKey) return null;
        const type = typeof d.type === 'string' && UNIT_TYPES.includes(d.type)
            ? (d.type as StorageUnitType)
            : StorageUnitType.Mesh;
        return stripUndefined({
            key,
            number: typeof d.number === 'string' ? d.number : '',
            type,
            levelKey,
            area: optionalNumber(d.area),
            ceilingHeight: optionalNumber(d.ceilingHeight),
            blueprintLocations: d.blueprintLocations && typeof d.blueprintLocations === 'object'
                ? (d.blueprintLocations as { [imageKey: string]: BlueprintLocation })
                : undefined,
            photos: d.photos && typeof d.photos === 'object'
                ? (d.photos as { [photoKey: string]: PhotoModel })
                : undefined,
            comment: optionalString(d.comment),
            currentLeaseKey: optionalString(d.currentLeaseKey),
            createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
            createdWhen: typeof d.createdWhen === 'string' ? d.createdWhen : '',
            updatedBy: optionalString(d.updatedBy),
            updatedWhen: optionalString(d.updatedWhen),
        });
    }

    /** Every storage unit of a building, keyed by unit key; empty when there are none. */
    async getUnits(buildingKey: string): Promise<{ [unitKey: string]: StorageUnit }> {
        const snapshot = await get(ref(this.db, this.getUnitsPath(buildingKey)));
        return this.parseUnits(snapshot.exists() ? snapshot.val() : undefined);
    }

    /** Parses the raw `storageUnits` node of a building model; invalid units are skipped. */
    parseUnits(data: unknown): { [unitKey: string]: StorageUnit } {
        const units: { [unitKey: string]: StorageUnit } = {};
        if (!data || typeof data !== 'object') return units;
        for (const [unitKey, unitData] of Object.entries(data as Record<string, unknown>)) {
            const unit = this.parseStorageUnit(unitData, unitKey);
            if (unit) {
                units[unitKey] = unit;
            } else {
                console.warn(`Skipping invalid storage unit ${unitKey}`);
            }
        }
        return units;
    }

    // MARK: - Units

    private validateUnitInput(input: StorageUnitInput): StorageUnitInput {
        const number = input.number.trim().toUpperCase();
        if (!number) {
            throw new Error('Förrådsnummer krävs');
        }
        if (!UNIT_TYPES.includes(input.type)) {
            throw new Error('Ogiltig typ av förråd');
        }
        const levelKey = input.levelKey?.trim();
        if (!levelKey) {
            throw new Error('Våning krävs');
        }
        if (input.area !== undefined && !(input.area > 0)) {
            throw new Error('Yta måste vara större än 0');
        }
        if (input.ceilingHeight !== undefined && !(input.ceilingHeight > 0)) {
            throw new Error('Takhöjd måste vara större än 0');
        }
        return {
            number,
            type: input.type,
            levelKey,
            area: input.area === undefined ? undefined : Math.round(input.area * 10) / 10,
            ceilingHeight: input.ceilingHeight,
            comment: optionalString(input.comment?.trim()),
        };
    }

    async createUnit(
        buildingKey: string,
        input: StorageUnitInput,
        blueprintLocation?: BlueprintLocation
    ): Promise<StorageUnit> {
        const uid = await this.verifyAdmin('skapa förråd');
        const validated = this.validateUnitInput(input);

        const newRef = push(ref(this.db, this.getUnitsPath(buildingKey)));
        const key = newRef.key;
        if (!key) {
            throw new Error('Kunde inte skapa nyckel för förrådet');
        }

        const unit: StorageUnit = stripUndefined({
            key,
            ...validated,
            blueprintLocations: blueprintLocation
                ? { [blueprintLocation.key]: blueprintLocation }
                : undefined,
            createdBy: uid,
            createdWhen: formatDateForStorage(new Date()),
        });

        // Brand-new node: set() is safe, nothing nested exists yet.
        await set(ref(this.db, this.getUnitPath(buildingKey, key)), unit);
        recordActivity('storageUnit.created', { buildingKey, targetKey: key, label: unit.number });
        return unit;
    }

    async updateUnit(buildingKey: string, unitKey: string, input: StorageUnitInput): Promise<void> {
        const uid = await this.verifyAdmin('ändra förråd');
        const validated = this.validateUnitInput(input);
        await update(ref(this.db, this.getUnitPath(buildingKey, unitKey)), {
            ...nullForUndefined({ ...validated }),
            ...this.stamp(uid),
        });
        recordActivity('storageUnit.updated', { buildingKey, targetKey: unitKey, label: validated.number });
    }

    /** Sets or clears (location = null) the unit's marker for one level drawing. */
    async setUnitLocation(
        buildingKey: string,
        unitKey: string,
        imageKey: string,
        location: BlueprintLocation | null
    ): Promise<void> {
        const uid = await this.verifyAdmin('placera förråd');
        await update(ref(this.db, this.getUnitPath(buildingKey, unitKey)), {
            [`blueprintLocations/${imageKey}`]: location ? { ...location, key: imageKey } : null,
            ...this.stamp(uid),
        });
        recordActivity('storageUnit.updated', { buildingKey, targetKey: unitKey });
    }

    /**
     * Tombstones every photo, then removes the unit node. Per file and not
     * atomic — accepted for parking (plan review F6) and kept here.
     */
    async deleteUnit(buildingKey: string, unitKey: string): Promise<void> {
        await this.verifyAdmin('ta bort förråd');
        const unitPath = this.getUnitPath(buildingKey, unitKey);
        const snapshot = await get(ref(this.db, unitPath));
        if (!snapshot.exists()) {
            throw new Error('Förrådet hittades inte');
        }
        const raw = snapshot.val() as Record<string, unknown>;
        // A rented unit holds a lease; ended leases keep their label and never block.
        if (optionalString(raw.currentLeaseKey)) {
            throw new Error('Förrådet har ett aktivt hyresavtal');
        }
        const photos = raw.photos && typeof raw.photos === 'object' ? (raw.photos as Record<string, unknown>) : {};
        for (const photoKey of Object.keys(photos)) {
            await softDeleteFile({
                originalPath: this.getUnitPhotoPath(buildingKey, unitKey, photoKey),
                entityType: STORAGE_UNIT_PHOTO_ENTITY_TYPE,
            });
        }
        await remove(ref(this.db, unitPath));
        recordActivity('storageUnit.deleted', { buildingKey, targetKey: unitKey, label: optionalString(raw.number) });
    }

    // MARK: - Photos

    /** Guard + path config for usePhotoUpload; the hook does the actual upload. */
    async prepareUnitPhotoUpload(
        buildingKey: string,
        unitKey: string
    ): Promise<{ storagePath: (photoKey: string) => string; dbPath: (photoKey: string) => string }> {
        await this.verifyAdmin('ladda upp foton');
        const path = (photoKey: string) => this.getUnitPhotoPath(buildingKey, unitKey, photoKey);
        return { storagePath: path, dbPath: path };
    }

    async stampUnitUpdated(buildingKey: string, unitKey: string): Promise<void> {
        const uid = await this.verifyAdmin('ändra förråd');
        await update(ref(this.db, this.getUnitPath(buildingKey, unitKey)), this.stamp(uid));
    }

    async deleteUnitPhoto(buildingKey: string, unitKey: string, photoKey: string): Promise<void> {
        const uid = await this.verifyAdmin('ta bort foton');
        const unitPath = this.getUnitPath(buildingKey, unitKey);
        await softDeleteFile({
            originalPath: this.getUnitPhotoPath(buildingKey, unitKey, photoKey),
            entityType: STORAGE_UNIT_PHOTO_ENTITY_TYPE,
            extraUpdates: {
                [`${unitPath}/updatedBy`]: uid,
                [`${unitPath}/updatedWhen`]: formatDateForStorage(new Date()),
            },
        });
    }
}

// Export singleton instance
export const storageUnitsApi = new StorageUnitsApi();
